import React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import SensexTab from "./Sensex";
import BankNiftyTab from "./BankNifty";
import BankexTab from "./Bankex";
import JammuTab from "./Jammu";
import ParentOrderAlignmentPanel from "../components/ParentOrderAlignmentPanel";
import SecretDhanExpiryDecayDesk from "./SecretDhanExpiryDecayDesk";
import DhanMarketReactionGrid from "./DhanMarketReactionGrid";
import DhanStrikeMarketReactionGrid from "./DhanStrikeMarketReactionGrid";
import DhanExpiryWriterMatrix from "./dhanExpiryWriterMatrix";


// ── Credentials — NIFTY only ──────────────────────────────────

const API_BASE = window.location.hostname === "localhost" ? "http://localhost:5000" : "";
const REFRESH_SEC = 10;

// ── Index config — NIFTY only ─────────────────────────────────
const NIFTY_INDEX = { id: 13, seg: "IDX_I", label: "NIFTY", range: 500 };

// ── Pages ─────────────────────────────────────────────────────
const PAGES = [
  { id: "nifty", label: "NIFTY", dot: "#38bdf8" },
  { id: "sensex",      label: "SENSEX",       dot: "#a78bfa" },
  { id: "banknifty", label: "BANKNIFTY", dot: "#38bdf8" },
  { id: "bankex", label: "BANKEX", dot: "#38bdf8" },
  { id: "jammu",       label: "JAMMU",       dot: "#00ffe7" },
  { id: "dhan-expiry-decay", label: "DHAN DECAY", dot: "#22c55e" },
  { id: "dhan-market-reaction", label: "DHAN MARKET", dot: "#22c55e" },
  { id: "dhan-strike-reaction", label: "DHAN STRIKES", dot: "#f7c948" },
  { id: "dhan-writer-matrix", label: "DHAN WRITERS", dot: "#22c55e" },

];

// ── Formatters ────────────────────────────────────────────────
const fmtVolCr = (v) => v != null ? (v / 1e7).toFixed(2) : "—";
const fmtOI    = (v) => v != null ? Number(v).toLocaleString("en-IN") : "0";
const fmtLTP   = (v) => (v != null && Number(v) !== 0) ? Number(v).toFixed(2) : "—";
const fmtTime  = (d) => d ? new Date(d).toLocaleTimeString("en-IN", { hour12: false }) : "";

// ── Styles ────────────────────────────────────────────────────
const C = {
  bg:     "#04090f",
  panel:  "#080f1a",
  card:   "#0a1628",
  border: "#0f2744",
  accent: "#38bdf8",
  green:  "#22c55e",
  red:    "#ef4444",
  amber:  "#f59e0b",
  purple: "#a78bfa",
  text:   "#cbd5e1",
  muted:  "#334155",
  dim:    "#1e3a5f",
};

const S = {
  page:  { minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace", fontSize: 13 },
  card:  { background: C.card, border: `1px solid ${C.border}`, borderRadius: 6 },
  label: { fontSize: 9, color: C.muted, letterSpacing: 3, textTransform: "uppercase", marginBottom: 5 },
  th:    { padding: "10px 14px", textAlign: "right", color: C.muted, fontSize: 9, letterSpacing: 2, fontWeight: "normal", borderBottom: `1px solid ${C.border}` },
  td:    { padding: "9px 14px", textAlign: "right", fontSize: 12, borderBottom: `1px solid #060d18` },
};

// ── Live spot fetcher ─────────────────────────────────────────
async function fetchLiveSpot(underlyingId, accessToken, clientId, segment = "IDX_I") {
  try {
    const res = await fetch(`${API_BASE}/api/options/live-spot`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ accessToken, clientId, underlyingId, segment }),
    });
    const d = await res.json();
    return d.spot || null;
  } catch { return null; }
}

function roundNiftySpotTo50(value) {
  const spot = Number(value || 0);
  if (!spot) return "";
  return String(Math.round(spot / 50) * 50);
}

function roundIndexSpotTo100(value) {
  const spot = Number(value || 0);
  if (!spot) return "";
  return String(Math.round(spot / 100) * 100);
}

// ── Generic fetch with retry ──────────────────────────────────
async function safeFetch(url, body, retries = 2) {
  for (let i = 0; i <= retries; i++) {
    try {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await res.json();
      if (d.error && i < retries) { await new Promise(r => setTimeout(r, 2000)); continue; }
      return d;
    } catch (e) {
      if (i === retries) throw e;
      await new Promise(r => setTimeout(r, 2000));
    }
  }
}

// ════════════════════════════════════════════════════════════════
// REUSABLE CONTROLS
// ════════════════════════════════════════════════════════════════
function Controls({
  label,
  spotPrice,
  setSpotPrice,
  clientId,
  setClientId,
  accessToken,
  setAccessToken,
  running,
  loading,
  error,
  countdown,
  lastUpdated,
  onStart,
  onStop,
  onFetchSpot,
  fetchingSpot,
  refreshSec,
  accentColor = C.accent
}) {
  return (
    <div style={{ background: C.panel, border: `1px solid ${C.border}`, borderRadius: 8, padding: "16px 20px", marginBottom: 20 }}>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div>
  <div style={S.label}>CLIENT ID</div>
  <input
    type="text"
    value={clientId}
    onChange={e => setClientId(e.target.value)}
    disabled={false}
    style={{
      background: "#060d18",
      border: `1px solid ${C.dim}`,
      color: accentColor,
      padding: "8px 12px",
      borderRadius: 4,
      fontSize: 12,
      width: 140,
    }}
  />
</div>

<div>
  <div style={S.label}>ACCESS TOKEN</div>
  <input
    type="text"
    value={accessToken}
    onChange={e => setAccessToken(e.target.value)}
    disabled={false}
    style={{
      background: "#060d18",
      border: `1px solid ${C.dim}`,
      color: accentColor,
      padding: "8px 12px",
      borderRadius: 4,
      fontSize: 12,
      width: 260,
    }}
  />
</div>

        <div>
          <div style={S.label}>{label} SPOT PRICE</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="number"
              value={spotPrice}
              onChange={e => setSpotPrice(e.target.value)}
              disabled={running}
              style={{ background: "#060d18", border: `1px solid ${C.dim}`, color: accentColor, padding: "8px 12px", borderRadius: 4, fontSize: 16, fontFamily: "inherit", width: 120, fontWeight: "bold" }}
            />
            <button
              onClick={onFetchSpot}
              disabled={running || fetchingSpot}
              title="Auto-fetch live spot from market"
              style={{ background: fetchingSpot ? C.muted : "#0f2040", border: `1px solid ${C.dim}`, color: accentColor, padding: "8px 10px", borderRadius: 4, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}
            >
              {fetchingSpot ? "⟳" : "⟲ LIVE"}
            </button>
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {!running ? (
            <button onClick={onStart} disabled={loading} style={{
              background: `linear-gradient(135deg, #15803d, #16a34a)`,
              border: "none", color: "#fff", padding: "9px 28px", borderRadius: 4,
              cursor: "pointer", fontFamily: "inherit", letterSpacing: 3, fontWeight: "bold", fontSize: 12,
              boxShadow: "0 0 12px #16a34a40",
            }}>▶ START</button>
          ) : (
            <button onClick={onStop} style={{
              background: `linear-gradient(135deg, #b91c1c, #dc2626)`,
              border: "none", color: "#fff", padding: "9px 28px", borderRadius: 4,
              cursor: "pointer", fontFamily: "inherit", letterSpacing: 3, fontWeight: "bold", fontSize: 12,
              boxShadow: "0 0 12px #dc262640",
            }}>■ STOP</button>
          )}
        </div>

        {running && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", minWidth: 60 }}>
            <div style={S.label}>REFRESH IN</div>
            <div style={{ fontSize: 24, color: accentColor, fontWeight: "bold", lineHeight: 1 }}>{countdown}s</div>
          </div>
        )}

        <div style={{ marginLeft: "auto", display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
          {loading && <span style={{ fontSize: 11, color: C.amber, letterSpacing: 2, animation: "pulse 1s infinite" }}>⟳ FETCHING DATA...</span>}
          {error   && <span style={{ fontSize: 11, color: C.red }}>⚠ {error}</span>}
          {lastUpdated && !loading && <span style={{ fontSize: 10, color: C.muted }}>UPDATED {fmtTime(lastUpdated)}</span>}
          {running && !loading && !error && <span style={{ fontSize: 10, color: C.green }}>● LIVE — AUTO REFRESH {refreshSec || REFRESH_SEC}s</span>}
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// SENTIMENT BAR
// ════════════════════════════════════════════════════════════════
function SentimentBar({ ceVal, peVal, ceLabel, peLabel, title }) {
  const total = (ceVal || 0) + (peVal || 0);
  if (!total) return null;
  const cePct = ((ceVal / total) * 100).toFixed(1);
  return (
    <div style={{ marginBottom: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 4 }}>
        <span style={{ color: C.green }}>{ceLabel}: {ceVal}</span>
        <span style={{ color: C.dim, fontSize: 8 }}>{title}</span>
        <span style={{ color: C.red }}>{peLabel}: {peVal}</span>
      </div>
      <div style={{ height: 6, background: C.border, borderRadius: 3, overflow: "hidden", display: "flex" }}>
        <div style={{ width: `${cePct}%`, background: `linear-gradient(90deg, ${C.green}, #16a34a)`, transition: "width 0.8s ease" }} />
        <div style={{ flex: 1, background: `linear-gradient(90deg, #b91c1c, ${C.red})` }} />
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// NIFTY ITM SCANNER
// ════════════════════════════════════════════════════════════════
function NiftyITMPage({ state, setState, refreshSec }) {
  const liveRefreshSec = Number(refreshSec || REFRESH_SEC);
  const { spotPrice, clientId, accessToken, running, loading, data, error, countdown, lastUpdated, fetchingSpot } = state;
  const set = useCallback((k, v) => setState(s => ({ ...s, [k]: v })), [setState]);
  const ivRef = useRef(null), cdRef = useRef(null);

  const fetchChain = useCallback(async () => {
    set("loading", true);
    try {
      const expData = await safeFetch(`${API_BASE}/api/options/expiry`, {
        accessToken,
        clientId,
        underlyingId: NIFTY_INDEX.id, segment: NIFTY_INDEX.seg,
      });
      if (!expData?.expiry) throw new Error("No expiry found");

      const d = await safeFetch(`${API_BASE}/api/options/chain`, {
        accessToken,
        clientId,
        expiry: expData.expiry, spotPrice: parseFloat(spotPrice),
        underlyingId: NIFTY_INDEX.id, segment: NIFTY_INDEX.seg, range: NIFTY_INDEX.range,
      });
      if (d?.error) {
  if (d.error.toLowerCase().includes("token") || d.error.toLowerCase().includes("auth")) {
    set("running", false);
    clearInterval(ivRef.current);
    clearInterval(cdRef.current);
    ivRef.current = null;
    cdRef.current = null;
    set("error", "TOKEN EXPIRED — PLEASE UPDATE");
    return;
  }
  throw new Error(d.error);
}
      set("data", d);
      set("error", "");
      set("lastUpdated", new Date().toISOString());
      set("countdown", liveRefreshSec);
    } catch (e) {
      set("error", e.message?.slice(0, 60));
    } finally { set("loading", false); }
  }, [spotPrice]);

  const start = async () => {
    set("running", true); set("error", "");
    await fetchChain();
    if (ivRef.current) clearInterval(ivRef.current);
    if (cdRef.current) clearInterval(cdRef.current);
    ivRef.current = setInterval(fetchChain, liveRefreshSec * 1000);
    cdRef.current = setInterval(() =>
      setState(s => ({ ...s, countdown: s.countdown <= 1 ? liveRefreshSec : s.countdown - 1 }))
    , 1000);
  };

  const stop = () => {
    clearInterval(ivRef.current); clearInterval(cdRef.current);
    ivRef.current = null; cdRef.current = null;
    set("running", false); set("countdown", liveRefreshSec);
  };

  useEffect(() => {
  if (
  state.running &&
  !ivRef.current &&
  state.accessToken?.length > 5 &&
  state.clientId?.length > 3
){
    start();
  }

  return () => {
    clearInterval(ivRef.current);
    clearInterval(cdRef.current);
  };
}, []);

  const onFetchSpot = async () => {
    set("fetchingSpot", true);
    const spot = await fetchLiveSpot(NIFTY_INDEX.id, accessToken, clientId);
    if (spot) set("spotPrice", roundNiftySpotTo50(spot));
    set("fetchingSpot", false);
  };

  const getSentiment = (s) => {
    if (!s) return null;
    const v = s.ceVolumeCr > s.peVolumeCr ? 1 : s.ceVolumeCr < s.peVolumeCr ? -1 : 0;
    const o = s.ceOI > s.peOI ? 1 : s.ceOI < s.peOI ? -1 : 0;
    const t = v + o;
    if (t >= 1)  return { label: "CALL BUYERS & PUT SELLERS DOMINANT — BULLISH", color: C.green, arrow: "▲" };
    if (t <= -1) return { label: "PUT BUYERS & CALL SELLERS DOMINANT — BEARISH",  color: C.red,   arrow: "▼" };
    return { label: "BALANCED — NO CLEAR DOMINANCE", color: C.amber, arrow: "◆" };
  };
  const sentiment = data ? getSentiment(data.summary) : null;

  return (
    <div>
      <Controls
        label="NIFTY" spotPrice={spotPrice} setSpotPrice={v => set("spotPrice", v)}
        running={running} loading={loading} error={error} countdown={countdown}
        lastUpdated={lastUpdated} fetchingSpot={fetchingSpot}
        onStart={start} onStop={stop} onFetchSpot={onFetchSpot}
        accentColor={C.accent}
        clientId={clientId}
        setClientId={v => set("clientId", v)}
        accessToken={accessToken}
        setAccessToken={v => set("accessToken", v)}

      />

      {sentiment && (
        <div style={{ ...S.card, borderColor: sentiment.color + "30", padding: "16px 20px", marginBottom: 16 }}>
          <div style={S.label}>MARKET SENTIMENT — NIFTY ITM ANALYSIS</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 16 }}>
            <span style={{ fontSize: 24, color: sentiment.color }}>{sentiment.arrow}</span>
            <span style={{ fontSize: 14, color: sentiment.color, fontWeight: "bold", letterSpacing: 2 }}>{sentiment.label}</span>
          </div>
          <SentimentBar ceVal={data.summary.ceVolumeCr} peVal={data.summary.peVolumeCr}
            ceLabel={`CE VOL ${data.summary.ceVolumeCr} Cr`} peLabel={`PE VOL ${data.summary.peVolumeCr} Cr`} title="VOLUME" />
          <SentimentBar ceVal={data.summary.ceOI} peVal={data.summary.peOI}
            ceLabel={`CE OI ${fmtOI(data.summary.ceOI)}`} peLabel={`PE OI ${fmtOI(data.summary.peOI)}`} title="OPEN INTEREST" />
        </div>
      )}

      {sentiment && (
        <ParentOrderAlignmentPanel
          title="NIFTY ITM PARENT ORDER ALIGNMENT"
          marketSide={sentiment.label.includes("BULLISH") ? "BUY" : sentiment.label.includes("BEARISH") ? "SELL" : ""}
          marketMetrics={{
            ceVolume: data?.summary?.ceVolumeCr,
            peVolume: data?.summary?.peVolumeCr,
            callSellers: data?.summary?.ceOI,
            putSellers: data?.summary?.peOI,
            pcr: data?.summary?.ceOI > 0 ? Number((data.summary.peOI / data.summary.ceOI).toFixed(2)) : 0,
          }}
        />
      )}

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 10, marginBottom: 16 }}>
          {[
            { label: "CE VOLUME",        value: `${(data.summary.ceVolumeCr * 0.01).toFixed(2)} Cr`, color: C.green },
            { label: "PE VOLUME",        value: `${(data.summary.peVolumeCr * 0.01).toFixed(2)} Cr`, color: C.red   },
            { label: "CE OPEN INTEREST", value: fmtOI(data.summary.ceOI),        color: C.green },
            { label: "PE OPEN INTEREST", value: fmtOI(data.summary.peOI),        color: C.red   },
          ].map(c => (
            <div key={c.label} style={{ ...S.card, padding: "14px 16px" }}>
              <div style={S.label}>{c.label}</div>
              <div style={{ fontSize: 18, color: c.color, fontWeight: "bold", marginTop: 4 }}>{c.value}</div>
            </div>
          ))}
        </div>
      )}

      {data && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
          {[
            { title: "▲ TOP 10 ITM CALLS — NIFTY CE", rows: data.calls, color: C.green },
            { title: "▼ TOP 10 ITM PUTS — NIFTY PE",  rows: data.puts,  color: C.red   },
          ].map(t => (
            <div key={t.title} style={{ ...S.card, overflow: "hidden" }}>
              <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: t.color, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>
                {t.title}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr>{["STRIKE", "LTP", "VOLUME (Cr)", "OI"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
                </thead>
                <tbody>
                  {t.rows.map((r, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#070e1a" : C.card }}>
                      <td style={{ ...S.td, color: t.color, fontWeight: "bold" }}>{r.strikePrice}</td>
                      <td style={{ ...S.td, color: C.muted }}>{fmtLTP(r.ltp)}</td>
                      <td style={{ ...S.td, color: C.text }}>{fmtVolCr(r.volume)}</td>
                      <td style={{ ...S.td, color: C.text }}>{fmtOI(r.oi)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {!data && !loading && (
        <div style={{ textAlign: "center", padding: "100px 0", color: C.dim }}>
          <div style={{ fontSize: 56, marginBottom: 16, color: C.accent, opacity: 0.3 }}>⬡</div>
          <div style={{ fontSize: 11, letterSpacing: 4, color: C.muted }}>PRESS ▶ START TO BEGIN LIVE SCAN</div>
          <div style={{ fontSize: 10, letterSpacing: 2, color: C.dim, marginTop: 8 }}>AUTO REFRESHES EVERY {liveRefreshSec} SECONDS</div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// OI ANALYSIS PAGE
// ════════════════════════════════════════════════════════════════
function OIAnalysisPage({ state, setState, refreshSec }) {
  const liveRefreshSec = Number(refreshSec || REFRESH_SEC);
  const { spotPrice, clientId, accessToken, running, loading, data, error, countdown, view, lastUpdated, fetchingSpot } = state;
  const set = useCallback((k, v) => setState(s => ({ ...s, [k]: v })), [setState]);
  const ivRef = useRef(null), cdRef = useRef(null);

  const fetchOI = useCallback(async () => {
    set("loading", true);
    try {
      const expData = await safeFetch(`${API_BASE}/api/options/expiry`, {
        accessToken,
        clientId,
        underlyingId: NIFTY_INDEX.id, segment: NIFTY_INDEX.seg,
      });
      if (!expData?.expiry) throw new Error("No expiry found");

      const d = await safeFetch(`${API_BASE}/api/options/oi-analysis`, {
        accessToken,
        clientId,
        expiry: expData.expiry, spotPrice: parseFloat(spotPrice),
      });
      if (d?.error) {
  if (d.error.toLowerCase().includes("token") || d.error.toLowerCase().includes("auth")) {
    set("running", false);
    clearInterval(ivRef.current);
    clearInterval(cdRef.current);
    ivRef.current = null;
    cdRef.current = null;
    set("error", "TOKEN EXPIRED — PLEASE UPDATE");
    return;
  }
  throw new Error(d.error);
}
      set("data", d);
      set("error", "");
      set("lastUpdated", new Date().toISOString());
      set("countdown", liveRefreshSec);
    } catch (e) { set("error", e.message?.slice(0, 60)); }
    finally { set("loading", false); }
  }, [spotPrice]);

  const start = async () => {
    set("running", true); set("error", "");
    await fetchOI();
    if (ivRef.current) clearInterval(ivRef.current);
    if (cdRef.current) clearInterval(cdRef.current);
    ivRef.current = setInterval(fetchOI, liveRefreshSec * 1000);
    cdRef.current = setInterval(() =>
      setState(s => ({ ...s, countdown: s.countdown <= 1 ? liveRefreshSec : s.countdown - 1 }))
    , 1000);
  };

  const stop = () => {
    clearInterval(ivRef.current); clearInterval(cdRef.current);
    ivRef.current = null; cdRef.current = null;
    set("running", false); set("countdown", liveRefreshSec);
  };

  useEffect(() => {
  if (state.running && !ivRef.current && state.accessToken && state.clientId) {
    start();
  }

  return () => {
    clearInterval(ivRef.current);
    clearInterval(cdRef.current);
  };
}, []);

  const onFetchSpot = async () => {
    set("fetchingSpot", true);
    const spot = await fetchLiveSpot(NIFTY_INDEX.id, accessToken, clientId);
    if (spot) set("spotPrice", roundNiftySpotTo50(spot));
    set("fetchingSpot", false);
  };

  const rows = data
    ? view === "calls"
      ? data.calls || []
      : data.puts || []
    : [];
  const pcrColor = !data ? C.muted : data.pcr < 0.7 ? C.red : data.pcr > 1.5 ? C.green : data.pcr <= 1.0 ? "#f87171" : "#86efac";
  const labelColor = l => l === "ATM" ? C.amber : l === "ITM" ? (view === "calls" ? C.green : C.red) : l === "OTM" ? (view === "calls" ? C.red : C.green) : C.muted;
  const ceOIChange = data ? data.calls.reduce((s, r) => s + (r.oiChange || 0), 0) : 0;
  const peOIChange = data ? data.puts.reduce((s,  r) => s + (r.oiChange || 0), 0) : 0;

  const oiSig = (() => {
    if (!data) return null;
    if (ceOIChange > 0 && peOIChange > 0)
      return ceOIChange > peOIChange
        ? { label: "CALL WRITERS ADDING — BEARISH SHIFT",                     color: C.red,   arrow: "▼" }
        : { label: "PUT WRITERS ADDING — BULLISH SHIFT",                      color: C.green, arrow: "▲" };
    if (ceOIChange > 0 && peOIChange <= 0)
      return { label: "CALL WRITERS ADDING · PUT WRITERS EXITING — BEARISH",  color: C.red,   arrow: "▼" };
    if (peOIChange > 0 && ceOIChange <= 0)
      return { label: "PUT WRITERS ADDING · CALL WRITERS EXITING — BULLISH",  color: C.green, arrow: "▲" };
    if (ceOIChange < 0 && peOIChange < 0)
      return { label: "BOTH WRITERS EXITING — UNWIND / REVERSAL POSSIBLE",    color: C.amber, arrow: "◆" };
    return { label: "NEUTRAL OI CHANGE", color: C.muted, arrow: "◆" };
  })();

  // ── TOP SUPPORT & RESISTANCE ZONES ──
const getTopZones = (data) => {
  if (!data) return { supports: [], resistances: [] };

  const resistances = [...(data.calls || [])]
    .sort((a, b) => (b.oi || 0) - (a.oi || 0))
    .slice(0, 4);

  const supports = [...(data.puts || [])]
    .sort((a, b) => (b.oi || 0) - (a.oi || 0))
    .slice(0, 4);

  return { supports, resistances };
};

const { supports, resistances } = data
  ? getTopZones(data)
  : { supports: [], resistances: [] };

  return (
    <div>
      <style>{`
        @keyframes sellerZoneBlink {
          0%, 100% { opacity: 1; box-shadow: none; }
          50% { opacity: .35; box-shadow: 0 0 10px currentColor; }
        }

        .seller-zone-change {
          display: inline-block;
          margin-top: 4px;
          padding: 2px 7px;
          border: 1px solid currentColor;
          border-radius: 4px;
          font-size: 10px;
          font-weight: bold;
          animation: sellerZoneBlink .7s ease-in-out infinite;
        }
      `}</style>
      
      <Controls
        label="NIFTY" spotPrice={spotPrice} setSpotPrice={v => set("spotPrice", v)}
        running={running} loading={loading} error={error} countdown={countdown}
        lastUpdated={lastUpdated} fetchingSpot={fetchingSpot}
        onStart={start} onStop={stop} onFetchSpot={onFetchSpot}
        accentColor={C.amber}
        clientId={clientId}
        setClientId={v => set("clientId", v)}
        accessToken={accessToken}
        setAccessToken={v => set("accessToken", v)}
        refreshSec={liveRefreshSec}
      />

      {data && (
        <div style={{ ...S.card, borderColor: pcrColor + "40", padding: "20px", marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr 1fr", gap: 24, alignItems: "center" }}>
            <div>
              <div style={S.label}>PCR RATIO</div>
              <div style={{ fontSize: 48, color: pcrColor, fontWeight: "bold", lineHeight: 1 }}>{data.pcr}</div>
              <div style={{ fontSize: 9, color: C.muted, marginTop: 4, letterSpacing: 1 }}>PE OI ÷ CE OI</div>
            </div>
            <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24 }}>
              <div style={S.label}>SIGNAL</div>
              <div style={{ fontSize: 18, color: pcrColor, fontWeight: "bold", letterSpacing: 2, marginBottom: 6 }}>{data.pcrSignal}</div>
              <div style={{ fontSize: 11, color: C.text, lineHeight: 1.6 }}>{data.pcrDesc}</div>
            </div>
            <div style={{ borderLeft: `1px solid ${C.border}`, paddingLeft: 24, display: "flex", flexDirection: "column", gap: 12 }}>
              <div>
                <div style={S.label}>CALL SELLERS (TOTAL CE OI)</div>
                <div style={{ fontSize: 18, color: C.red, fontWeight: "bold" }}>{fmtOI(data.totalCeOI)}</div>
              </div>
              <div>
                <div style={S.label}>PUT SELLERS (TOTAL PE OI)</div>
                <div style={{ fontSize: 18, color: C.green, fontWeight: "bold" }}>{fmtOI(data.totalPeOI)}</div>
              </div>
            </div>
          </div>
          <div style={{ marginTop: 20 }}>
            <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 6 }}>
              <span style={{ color: C.red }}>← BEARISH (PCR &lt; 0.7)</span>
              <span>NEUTRAL (0.7 – 1.5)</span>
              <span style={{ color: C.green }}>(PCR &gt; 1.5) BULLISH →</span>
            </div>
            <div style={{ position: "relative", height: 12, background: `linear-gradient(to right, ${C.red}, ${C.amber} 40%, ${C.green})`, borderRadius: 6 }}>
              <div style={{
                position: "absolute",
                left: `${Math.min(Math.max((data.pcr / 2) * 100, 1), 99)}%`,
                top: -4, transform: "translateX(-50%)",
                width: 5, height: 20, background: "#fff", borderRadius: 3,
                boxShadow: "0 0 8px rgba(255,255,255,0.8)"
              }} />
            </div>
          </div>
        </div>
      )}

      {data && (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 14 }}>

    {/* RESISTANCE */}
    <div style={{ ...S.card, padding: "14px 16px" }}>
      <div style={{ ...S.label, color: C.red }}>TOP 4 RESISTANCE (CE OI)</div>
      {resistances.map(r => {
        const change = Number(r.oiChange || 0);

        return (
          <div key={`res-${r.strike}`} style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "6px 0",
            borderBottom: "1px solid #0a1628"
          }}>
            <span style={{ color: C.red, fontWeight: "bold" }}>{r.strike}</span>

            <div style={{ textAlign: "right" }}>
              <div>{fmtOI(r.oi)}</div>
              <span
                key={`res-change-${r.strike}-${change}`}
                className={change !== 0 ? "seller-zone-change" : ""}
                style={{ color: change > 0 ? C.red : change < 0 ? C.green : C.muted }}
              >
                {change > 0
                  ? `[▲ +${fmtOI(change)} ADDED]`
                  : change < 0
                    ? `[▼ ${fmtOI(Math.abs(change))} LEFT]`
                    : "[• 0 CHANGE]"}
              </span>
            </div>
          </div>
        );
      })}
    </div>

    {/* SUPPORT */}
    <div style={{ ...S.card, padding: "14px 16px" }}>
      <div style={{ ...S.label, color: C.green }}>TOP 4 SUPPORT (PE OI)</div>
      {supports.map(r => {
        const change = Number(r.oiChange || 0);

        return (
          <div key={`sup-${r.strike}`} style={{
            display: "flex", justifyContent: "space-between",
            alignItems: "center", padding: "6px 0",
            borderBottom: "1px solid #0a1628"
          }}>
            <span style={{ color: C.green, fontWeight: "bold" }}>{r.strike}</span>

            <div style={{ textAlign: "right" }}>
              <div>{fmtOI(r.oi)}</div>
              <span
                key={`sup-change-${r.strike}-${change}`}
                className={change !== 0 ? "seller-zone-change" : ""}
                style={{ color: change > 0 ? C.green : change < 0 ? C.red : C.muted }}
              >
                {change > 0
                  ? `[▲ +${fmtOI(change)} ADDED]`
                  : change < 0
                    ? `[▼ ${fmtOI(Math.abs(change))} LEFT]`
                    : "[• 0 CHANGE]"}
              </span>
            </div>
          </div>
        );
      })}
    </div>

  </div>
)}

      {oiSig && (
        <div style={{ ...S.card, borderColor: oiSig.color + "40", padding: "16px 20px", marginBottom: 14 }}>
          <div style={S.label}>OI CHANGE DIRECTION — REAL-TIME SHIFT</div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
            <span style={{ fontSize: 24, color: oiSig.color }}>{oiSig.arrow}</span>
            <span style={{ fontSize: 13, color: oiSig.color, fontWeight: "bold", letterSpacing: 2 }}>{oiSig.label}</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
            {[
              { label: "CE OI CHANGE (CALL WRITERS)", val: ceOIChange, addText: "Resistance building ▲", exitText: "Resistance weakening ▼", col: v => v > 0 ? C.red : C.green },
              { label: "PE OI CHANGE (PUT WRITERS)",  val: peOIChange, addText: "Support building ▲",    exitText: "Support weakening ▼",    col: v => v > 0 ? C.green : C.red },
            ].map(r => (
              <div key={r.label} style={{ background: "#060d18", borderRadius: 6, padding: "12px 16px" }}>
                <div style={S.label}>{r.label}</div>
                <div style={{ fontSize: 20, color: r.col(r.val), fontWeight: "bold", marginBottom: 4 }}>
                  {r.val >= 0 ? `▲ +${fmtOI(r.val)}` : `▼ ${fmtOI(r.val)}`}
                </div>
                <div style={{ fontSize: 10, color: C.muted }}>{r.val > 0 ? r.addText : r.exitText}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data && (() => {
        const ceW = data.totalCeOI, peW = data.totalPeOI, total = ceW + peW;
        const ceP = total > 0 ? ((ceW / total) * 100).toFixed(1) : 50;
        const peP = total > 0 ? ((peW / total) * 100).toFixed(1) : 50;
        const s = ceW > peW
          ? { label: "CALL SELLERS STRONGER", sub: "Resistance building — bearish pressure", color: C.red, arrow: "▼" }
          : peW > ceW
          ? { label: "PUT SELLERS STRONGER",  sub: "Support building — bullish pressure",    color: C.green, arrow: "▲" }
          : { label: "EQUAL WRITER STRENGTH", sub: "Balanced — no directional bias",          color: C.amber, arrow: "◆" };
        return (
          <div style={{ ...S.card, borderColor: s.color + "40", padding: "16px 20px", marginBottom: 14 }}>
            <div style={S.label}>COMBINED WRITER STRENGTH</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
              <span style={{ fontSize: 22, color: s.color }}>{s.arrow}</span>
              <div>
                <div style={{ fontSize: 14, color: s.color, fontWeight: "bold", letterSpacing: 2 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: C.text, marginTop: 2 }}>{s.sub}</div>
              </div>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", ...S.label, marginBottom: 4 }}>
              <span style={{ color: C.red }}>CALL WRITERS {fmtOI(ceW)} ({ceP}%)</span>
              <span style={{ color: C.green }}>PUT WRITERS {fmtOI(peW)} ({peP}%)</span>
            </div>
            <div style={{ height: 10, background: C.border, borderRadius: 5, overflow: "hidden", display: "flex" }}>
              <div style={{ width: `${ceP}%`, background: `linear-gradient(90deg, #b91c1c, ${C.red})`, transition: "width 0.8s ease" }} />
              <div style={{ flex: 1, background: `linear-gradient(90deg, #15803d, ${C.green})` }} />
            </div>
          </div>
        );
      })()}

      {data && (
        <div style={{ display: "flex", marginBottom: 14, borderRadius: 6, overflow: "hidden", border: `1px solid ${C.border}`, width: "fit-content" }}>
          {[
            { id: "calls", label: "📞 CALL SELLERS (CE)", color: C.red   },
            { id: "puts",  label: "📉 PUT SELLERS (PE)",   color: C.green },
          ].map(tab => (
            <button key={tab.id} onClick={() => set("view", tab.id)} style={{
              padding: "8px 24px", fontSize: 11, letterSpacing: 2, fontFamily: "inherit", cursor: "pointer", border: "none",
              background: view === tab.id ? "#060d18" : C.card,
              color: view === tab.id ? tab.color : C.muted,
              borderBottom: view === tab.id ? `2px solid ${tab.color}` : "2px solid transparent",
              fontWeight: view === tab.id ? "bold" : "normal",
            }}>{tab.label}</button>
          ))}
        </div>
      )}

      {data && (
        <div style={{ ...S.card, overflow: "hidden" }}>
          <div style={{ background: "#060d18", padding: "10px 16px", fontSize: 10, color: view === "calls" ? C.red : C.green, letterSpacing: 3, fontWeight: "bold", borderBottom: `1px solid ${C.border}` }}>
            {view === "calls" ? "📞 CALL SELLERS — CE OI (1 ITM · ATM · 10 OTM)" : "📉 PUT SELLERS — PE OI (10 OTM · ATM · 1 ITM)"}
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>{["TYPE", "STRIKE", "LTP", "OI", "OI CHANGE", "VOLUME (Cr)"].map(h => <th key={h} style={S.th}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={i} style={{ background: r.label === "ATM" ? "#0d1e35" : i % 2 === 0 ? "#070e1a" : C.card }}>
                  <td style={{ ...S.td, textAlign: "left", paddingLeft: 16 }}>
                    <span style={{ fontSize: 9, fontWeight: "bold", letterSpacing: 1, color: labelColor(r.label), border: `1px solid ${labelColor(r.label)}30`, padding: "2px 7px", borderRadius: 3 }}>{r.label}</span>
                  </td>
                  <td style={{ ...S.td, color: r.label === "ATM" ? C.amber : C.text, fontWeight: r.label === "ATM" ? "bold" : "normal" }}>{r.strike}</td>
                  <td style={{ ...S.td, color: C.muted }}>{fmtLTP(r.ltp)}</td>
                  <td style={{ ...S.td, color: view === "calls" ? C.red : C.green, fontWeight: "bold" }}>{fmtOI(r.oi)}</td>
                  <td style={{ ...S.td, color: (r.oiChange || 0) > 0 ? C.green : (r.oiChange || 0) < 0 ? C.red : C.muted }}>
                    {(r.oiChange || 0) > 0 ? "▲ " : (r.oiChange || 0) < 0 ? "▼ " : ""}{fmtOI(Math.abs(r.oiChange || 0))}
                  </td>
                  <td style={{ ...S.td, color: C.text }}>{fmtVolCr(r.volume)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!data && !loading && (
        <div style={{ textAlign: "center", padding: "100px 0", color: C.dim }}>
          <div style={{ fontSize: 56, marginBottom: 16, color: C.amber, opacity: 0.3 }}>⬡</div>
          <div style={{ fontSize: 11, letterSpacing: 4, color: C.muted }}>PRESS ▶ START TO BEGIN OI ANALYSIS</div>
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN ADMIN PANEL
// ════════════════════════════════════════════════════════════════
const initState = (extra = {}) => ({
  spotPrice: "25150",
  accessToken: "",
  clientId: "",
  running: false,
  loading: false,
  data: null, error: "", countdown: REFRESH_SEC,
  lastUpdated: null, fetchingSpot: false, ...extra,
});

export default function AdminPanel() {
  const loginUser = JSON.parse(localStorage.getItem("ct_login_user") || "null");
  const userStoragePrefix = `secret_${loginUser?.id || loginUser?.username || "guest"}`;
  const storageKey = (key) => `${userStoragePrefix}_${key}`;
  const [secretRefreshSec, setSecretRefreshSec] = useState(() => {
    const saved = localStorage.getItem(storageKey("secretRefreshSec"));
    return Math.max(5, Number(saved || REFRESH_SEC));
  });
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState(() => {
  const savedPage = localStorage.getItem(storageKey("activePage")) || "nifty";
  return savedPage === "nifty-itm" || savedPage === "oi-analysis" ? "nifty" : savedPage;
});

  const [niftyView, setNiftyView] = useState(() => {
    const savedPage = localStorage.getItem(storageKey("activePage"));
    return savedPage === "oi-analysis" ? "oi-analysis" : "nifty-itm";
  });

  useEffect(() => {
    localStorage.setItem(
      storageKey("activePage"),
      activePage === "nifty" ? niftyView : activePage
    );
  }, [activePage, niftyView]);

  useEffect(() => {
  localStorage.setItem(storageKey("secretRefreshSec"), String(secretRefreshSec));
}, [secretRefreshSec]);

  const [niftyState, setNiftyState] = useState(() => {
  const saved = localStorage.getItem(storageKey("niftyCreds"));
  return saved
    ? { ...initState({ spotPrice: "25150" }), ...JSON.parse(saved) }
    : initState({ spotPrice: "25150" });
});

const [oiState, setOiState] = useState(() => {
  const saved = localStorage.getItem(storageKey("oiCreds"));
  return saved
    ? { ...initState({ spotPrice: "25150", view: "calls" }), ...JSON.parse(saved) }
    : initState({ spotPrice: "25150", view: "calls" });
});
  
  const [sensexState, setSensexState] = useState(() => {
  const saved = localStorage.getItem(storageKey("sensexCreds"));
  return saved
    ? { ...initState({ spotPrice: "81200" }), ...JSON.parse(saved) }
    : initState({ spotPrice: "81200" });
});

const [bankNiftyState, setBankNiftyState] = useState(() => {
  const saved = localStorage.getItem(storageKey("bankNiftyCreds"));
  return saved
    ? { ...initState({ spotPrice: "55000" }), ...JSON.parse(saved) }
    : initState({ spotPrice: "55000" });
});

const [bankexState, setBankexState] = useState(() => {
  const saved = localStorage.getItem(storageKey("bankexCreds"));
  return saved
    ? { ...initState({ spotPrice: "62000" }), ...JSON.parse(saved) }
    : initState({ spotPrice: "62000" });
});

  useEffect(() => {
    if (!localStorage.getItem("ct_admin_auth")) navigate("/login");
  }, []);
  // Save NIFTY credentials
useEffect(() => {
  localStorage.setItem(storageKey("niftyCreds"), JSON.stringify({
    accessToken: niftyState.accessToken,
    clientId: niftyState.clientId,
    spotPrice: niftyState.spotPrice,
    running: niftyState.running
  }));
}, [niftyState]);

// Save OI credentials
useEffect(() => {
  localStorage.setItem(storageKey("oiCreds"), JSON.stringify({
    accessToken: oiState.accessToken,
    clientId: oiState.clientId,
    spotPrice: oiState.spotPrice,
    running: oiState.running,
    view: oiState.view
  }));
}, [oiState]);

  useEffect(() => {
    localStorage.setItem(storageKey("sensexCreds"), JSON.stringify({
      accessToken: sensexState.accessToken,
      clientId: sensexState.clientId,
      spotPrice: sensexState.spotPrice,
      running: sensexState.running,
    }));
  }, [sensexState]);

  useEffect(() => {
    localStorage.setItem(storageKey("bankNiftyCreds"), JSON.stringify({
      accessToken: bankNiftyState.accessToken,
      clientId: bankNiftyState.clientId,
      spotPrice: bankNiftyState.spotPrice,
      running: bankNiftyState.running,
    }));
  }, [bankNiftyState]);

  useEffect(() => {
    localStorage.setItem(storageKey("bankexCreds"), JSON.stringify({
      accessToken: bankexState.accessToken,
      clientId: bankexState.clientId,
      spotPrice: bankexState.spotPrice,
      running: bankexState.running,
    }));
  }, [bankexState]);

  const logout = () => {
    localStorage.removeItem("ct_login_token");
    localStorage.removeItem("ct_login_user");
    localStorage.removeItem("ct_admin_auth");
    navigate("/login", { replace: true });
  };

  const stateMap = {
    "nifty": {
      running: niftyState.running || oiState.running,
      countdown: niftyState.running ? niftyState.countdown : oiState.countdown,
      lastUpdated: niftyState.lastUpdated || oiState.lastUpdated,
    },
    "nifty-itm":   niftyState,
    "oi-analysis": oiState,
    "sensex":      sensexState,
    "banknifty": bankNiftyState,
    "bankex": bankexState,
  };

  useEffect(() => {
  if (loginUser?.role !== "admin") return;

  const token = localStorage.getItem("ct_login_token");
  if (!token) return;

  const applySecretSettings = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/login/admin/secret-settings`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) return;

      const settings = data.settings || {};
      const refreshSeconds = Math.max(5, Number(settings.refreshSeconds || REFRESH_SEC));
      setSecretRefreshSec(refreshSeconds);
      const clientId = settings.clientId || "";
      const accessToken = settings.accessToken || "";

      setNiftyState(s => ({ ...s, clientId, accessToken, countdown: refreshSeconds }));
      setOiState(s => ({ ...s, clientId, accessToken, countdown: refreshSeconds }));
      setSensexState(s => ({ ...s, clientId, accessToken, countdown: refreshSeconds }));
      setBankNiftyState(s => ({ ...s, clientId, accessToken, countdown: refreshSeconds }));
      setBankexState(s => ({ ...s, clientId, accessToken, countdown: refreshSeconds }));

      const [niftySpot, sensexSpot, bankNiftySpot, bankexSpot] = await Promise.all([
        fetchLiveSpot(13, accessToken, clientId),
        fetchLiveSpot(51, accessToken, clientId),
        fetchLiveSpot(25, accessToken, clientId),
        fetchLiveSpot(69, accessToken, clientId),
      ]);

      if (niftySpot) {
        const roundedNiftySpot = roundNiftySpotTo50(niftySpot);
        setNiftyState(s => ({ ...s, spotPrice: roundedNiftySpot }));
        setOiState(s => ({ ...s, spotPrice: roundedNiftySpot }));
      }

      if (sensexSpot) setSensexState(s => ({ ...s, spotPrice: roundIndexSpotTo100(sensexSpot) }));
      if (bankNiftySpot) setBankNiftyState(s => ({ ...s, spotPrice: roundIndexSpotTo100(bankNiftySpot) }));
      if (bankexSpot) setBankexState(s => ({ ...s, spotPrice: roundIndexSpotTo100(bankexSpot) }));
    } catch {}
  };

  applySecretSettings();
}, [loginUser?.role]);

  useEffect(() => {
  const clientId =
    niftyState.clientId ||
    oiState.clientId ||
    sensexState.clientId ||
    bankNiftyState.clientId ||
    bankexState.clientId;

  const accessToken =
    niftyState.accessToken ||
    oiState.accessToken ||
    sensexState.accessToken ||
    bankNiftyState.accessToken ||
    bankexState.accessToken;

  if (!clientId || !accessToken) return;

  let cancelled = false;

  const refreshSecretSpotPrices = async () => {
    const [niftySpot, sensexSpot, bankNiftySpot, bankexSpot] = await Promise.all([
      fetchLiveSpot(13, accessToken, clientId),
      fetchLiveSpot(51, accessToken, clientId),
      fetchLiveSpot(25, accessToken, clientId),
      fetchLiveSpot(69, accessToken, clientId),
    ]);

    if (cancelled) return;

    if (niftySpot) {
      const roundedNiftySpot = roundNiftySpotTo50(niftySpot);
      setNiftyState(s => ({ ...s, spotPrice: roundedNiftySpot }));
      setOiState(s => ({ ...s, spotPrice: roundedNiftySpot }));
    }

    if (sensexSpot) {
      setSensexState(s => ({ ...s, spotPrice: roundIndexSpotTo100(sensexSpot) }));
    }

    if (bankNiftySpot) {
      setBankNiftyState(s => ({ ...s, spotPrice: roundIndexSpotTo100(bankNiftySpot) }));
    }

    if (bankexSpot) {
      setBankexState(s => ({ ...s, spotPrice: roundIndexSpotTo100(bankexSpot) }));
    }
  };

  refreshSecretSpotPrices();

  const timer = setInterval(refreshSecretSpotPrices, secretRefreshSec * 1000);

  return () => {
    cancelled = true;
    clearInterval(timer);
  };
}, [
  niftyState.clientId,
  niftyState.accessToken,
  oiState.clientId,
  oiState.accessToken,
  sensexState.clientId,
  sensexState.accessToken,
  bankNiftyState.clientId,
  bankNiftyState.accessToken,
  bankexState.clientId,
  bankexState.accessToken,
  secretRefreshSec,
]);

  return (
    <div style={S.page}>

      {/* TOP BAR */}
      <div style={{
        background: `linear-gradient(180deg, #080f1a, #04090f)`,
        borderBottom: `1px solid ${C.border}`,
        padding: "0 24px",
        display: "flex", justifyContent: "space-between", alignItems: "stretch",
        position: "sticky", top: 0, zIndex: 100,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 20, padding: "8px 0" }}>
          <div>
            <div style={{ fontSize: 15, color: C.accent, fontWeight: "bold", letterSpacing: 4 }}>⬡ OC TERMINAL</div>
            <div style={{ fontSize: 8, color: C.muted, letterSpacing: 3 }}>OPTION CHAIN ANALYTICS PLATFORM</div>
          </div>
        </div>

        {/* Page tabs */}
        <div style={{ display: "flex", alignItems: "stretch" }}>
          {PAGES.map(p => {
            const isActive  = activePage === p.id;
            const isRunning = stateMap[p.id]?.running;
            return (
              <button 
                key={p.id} 
                className={isActive ? "terminal-tab-active" : ""}
                onClick={() => setActivePage(p.id)} 
                style={{
                  height: 52,          // was 68
                  padding: "0 8px",   // was "0 12px"
                  fontSize: 9,         // keep same text size
                  letterSpacing: 1.5,  // keep same
                  minWidth: 78,        // was 95
                  fontFamily: "inherit",
                  cursor: "pointer",
                  border: "none",
                  borderBottom: isActive ? `2px solid ${p.dot}` : "2px solid transparent",
                  background: "transparent",
                  color: isActive ? p.dot : C.muted,
                  fontWeight: isActive ? "bold" : "normal",
                  display: "flex",
                  justifyContent: "center",
                  textAlign: "center",
                  lineHeight: 1.4,     // was 1.6
                  alignItems: "center",
                  gap: 5,
                  transition: "color 0.2s",
                }}>
                {isRunning && <span style={{ width: 6, height: 6, borderRadius: "50%", background: C.green, boxShadow: `0 0 6px ${C.green}` }} />}
                {p.label}
              </button>
            );
          })}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 16, padding: "14px 0" }}>
          <div style={{ fontSize: 8, color: C.muted, letterSpacing: 1.5, textAlign: "right" }}>
            <div>REFRESH RATE</div>
            <div style={{ color: C.accent, fontSize: 10 }}>{secretRefreshSec}s</div>
          </div>
          <button onClick={logout} style={{
            background: "transparent", border: `1px solid ${C.red}40`, color: C.red,
            padding: "5px 10px", borderRadius: 4, cursor: "pointer", fontSize: 8, letterSpacing: 1.5, fontFamily: "inherit",
          }}>LOGOUT</button>
        </div>
      </div>

      {/* Status bar */}
      {/* Status bar */}
        <div style={{
          background: "#060d18",
          borderBottom: `1px solid ${C.border}`,
          padding: "5px 16px",
          display: "flex",
          justifyContent: "space-between",
          gap: 12,
          fontSize: 8,
          color: C.muted,
          letterSpacing: 1.5,
        }}>
        {PAGES.map(p => {
          const s = stateMap[p.id];
          return (
            <span key={p.id}>
              <span style={{ color: s?.running ? C.green : C.dim }}>●</span> {p.label}: {s?.running ? `LIVE · ${s.countdown}s` : "STOPPED"}
              {s?.lastUpdated ? ` · ${fmtTime(s.lastUpdated)}` : ""}
            </span>
          );
        })}
      </div>

      {/* Content — all 3 always mounted, display:none preserves state & intervals */}
      <div style={{ padding: "20px 24px", maxWidth: 1300, margin: "0 auto" }}>
        <div style={{ display: activePage === "jammu" ? "block" : "none" }}>
          <JammuTab
            secretClientId={niftyState.clientId || oiState.clientId || sensexState.clientId || bankNiftyState.clientId || bankexState.clientId}
            secretAccessToken={niftyState.accessToken || oiState.accessToken || sensexState.accessToken || bankNiftyState.accessToken || bankexState.accessToken}
            secretSpots={{
              nifty: niftyState.spotPrice,
              sensex: sensexState.spotPrice,
              banknifty: bankNiftyState.spotPrice,
              bankex: bankexState.spotPrice,
            }}
          />
        </div>

        <div style={{ display: activePage === "dhan-expiry-decay" ? "block" : "none" }}>
          <SecretDhanExpiryDecayDesk
            secretClientId={niftyState.clientId || oiState.clientId || sensexState.clientId || bankNiftyState.clientId || bankexState.clientId}
            secretAccessToken={niftyState.accessToken || oiState.accessToken || sensexState.accessToken || bankNiftyState.accessToken || bankexState.accessToken}
            secretSpots={{
              nifty: niftyState.spotPrice,
              sensex: sensexState.spotPrice,
              banknifty: bankNiftyState.spotPrice,
            }}
          />
        </div>

        <div style={{ display: activePage === "dhan-market-reaction" ? "block" : "none" }}>
          <DhanMarketReactionGrid
            secretClientId={niftyState.clientId}
            secretAccessToken={niftyState.accessToken}
            onOpenStrike={() => setActivePage("dhan-strike-reaction")}
          />
        </div>

        <div style={{ display: activePage === "dhan-writer-matrix" ? "block" : "none" }}>
          <DhanExpiryWriterMatrix
            secretClientId={niftyState.clientId}
            secretAccessToken={niftyState.accessToken}
          />
        </div>

        <div style={{ display: activePage === "dhan-strike-reaction" ? "block" : "none" }}>
          <DhanStrikeMarketReactionGrid
            secretClientId={niftyState.clientId}
            secretAccessToken={niftyState.accessToken}
            onOpenMarket={() => setActivePage("dhan-market-reaction")}
          />
        </div>

        <div style={{ display: activePage === "nifty" ? "block" : "none" }}>
          <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
            <button
              type="button"
              onClick={() => setNiftyView("nifty-itm")}
              style={{
                background: niftyView === "nifty-itm" ? "#0f2040" : "transparent",
                border: `1px solid ${niftyView === "nifty-itm" ? C.accent : C.border}`,
                color: niftyView === "nifty-itm" ? C.accent : C.muted,
                padding: "8px 18px",
                borderRadius: 4,
                cursor: "pointer",
                fontFamily: "inherit",
                letterSpacing: 2,
                fontSize: 10,
              }}
            >
              NIFTY ITM
            </button>

            <button
              type="button"
              onClick={() => setNiftyView("oi-analysis")}
              style={{
                background: niftyView === "oi-analysis" ? "#2a1f08" : "transparent",
                border: `1px solid ${niftyView === "oi-analysis" ? C.amber : C.border}`,
                color: niftyView === "oi-analysis" ? C.amber : C.muted,
                padding: "8px 18px",
                borderRadius: 4,
                cursor: "pointer",
                fontFamily: "inherit",
                letterSpacing: 2,
                fontSize: 10,
              }}
            >
              OI ANALYSIS
            </button>
          </div>

          <div style={{ display: niftyView === "nifty-itm" ? "block" : "none" }}>
            <NiftyITMPage state={niftyState} setState={setNiftyState} refreshSec={secretRefreshSec} />
          </div>

          <div style={{ display: niftyView === "oi-analysis" ? "block" : "none" }}>
            <OIAnalysisPage state={oiState} setState={setOiState} refreshSec={secretRefreshSec} />
          </div>
        </div>
        <div style={{ display: activePage === "sensex" ? "block" : "none" }}>
          <SensexTab state={sensexState} setState={setSensexState} refreshSec={secretRefreshSec} />
        </div>
        <div style={{ display: activePage === "banknifty" ? "block" : "none" }}>
          <BankNiftyTab state={bankNiftyState} setState={setBankNiftyState} refreshSec={secretRefreshSec} />
        </div>
        <div style={{ display: activePage === "bankex" ? "block" : "none" }}>
          <BankexTab state={bankexState} setState={setBankexState} refreshSec={secretRefreshSec} />
        </div>
      </div>
    </div>
  );
}